import _ from 'lodash';
import axios from 'axios';

/**
 * Check if any glob pattern in this array matches the given value.
 *
 * Pattern rules:
 *   'abc'  → exact match
 *   'abc*' → starts with 'abc'
 *   '*abc' → ends with 'abc'
 *   '*abc*' → contains 'abc'
 *
 * @function contains
 * @param {string} value - Value to test
 * @returns {boolean} True if at least one pattern matches
 *
 * @example
 * ['a*', 'b'].contains('aaaa')   // true  (starts with 'a')
 * ['*_end'].contains('test_end') // true  (ends with '_end')
 * ['*mid*'].contains('a_mid_b')  // true  (contains 'mid')
 * ['exact'].contains('exact')    // true  (exact match)
 * ['a*'].contains('bbb')         // false (no match)
 */
Object.defineProperty(Array.prototype, 'contains', {
    enumerable: false,
    writable: true,
    configurable: true,
    value: function (value) {
        const v = String(value);
        return this.some((item) => {
            const pattern = String(item);
            const hasPrefix = pattern.startsWith('*');
            const hasSuffix = pattern.endsWith('*');
            const keyword = pattern.replace(/^\*|\*$/g, '');
            if (hasPrefix && hasSuffix) return v.includes(keyword);
            if (hasPrefix) return v.endsWith(keyword);
            if (hasSuffix) return v.startsWith(keyword);
            return v === keyword;
        });
    },
});

/**
 * Filter items from target array using glob patterns in this array.
 * Uses the same pattern rules as `contains()`.
 *
 * @function globFrom
 * @param {string[]} targets - Array to filter from
 * @returns {string[]} Items that matched at least one pattern
 *
 * @example
 * ['a*', 'b'].globFrom(['aaaa', 'b', 'c'])              // ['aaaa', 'b']
 * ['*_end'].globFrom(['test_end', 'start_end', 'nope'])  // ['test_end', 'start_end']
 * ['*mid*'].globFrom(['a_mid_b', 'mid_x', 'nope'])       // ['a_mid_b', 'mid_x']
 */
Object.defineProperty(Array.prototype, 'globFrom', {
    enumerable: false,
    writable: true,
    configurable: true,
    value(targets) {
        return targets.filter((target) => this.contains(target));
    },
});

window._ = _.mixin({
    /**
     * PHP strtr equivalent for Lodash
     * @param {string} value - Target string to transform
     * @param {Object} pairs - Replacement map object
     * @returns {string} Transformed string
     */
    substitute: (value, pairs) => {
        return Object.entries(pairs).reduce((s, [from, to]) => s.replaceAll(from, to), value);
    },

    /**
     * Generate a random alphanumeric string
     *
     * @function randomString
     * @param {number} length - Desired string length (default: 5)
     * @param {object} [options] - Option settings
     * @param {boolean} [options.upper=false] - Include uppercase characters if true
     * @returns {string} Randomly generated string
     *
     * @example
     * _.randomString() // "a9xd2"
     * _.randomString(8) // "b2e9fa1c"
     * _.randomString(8, { upper: true }) // "A9dE3fQ2"
     */
    randomString: (length = 5, options = { upper: false }) => {
        let chars = 'abcdefghijklmnopqrstuvwxyz0123456789';

        // Add uppercase characters if requested
        if (options.upper) {
            chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        }

        // Generate random characters allowing repetition
        return _.times(length, () => _.sample(chars)).join('');
    },

    /**
     * Find the closest numeric value in a given set.
     *
     * @function findClosestValue
     * @param {number[]} set - Array of numeric values to search within.
     * @param {number} target - The target number used for comparison.
     * @returns {number|null} The value from the set closest to the target.
     *          Returns null if the set is empty or invalid.
     *
     * @example
     * _.findClosestValue([10, 20, 30], 22) // 20
     * _.findClosestValue([5, 12, 18], 17) // 18
     * _.findClosestValue([], 10) // null
     */
    findClosestValue: (set, target) => {
        return !_.isArray(set) || set.length === 0
            ? null //
            : _.minBy(set, (v) => Math.abs(v - target));
    },

    /** Find option whose `value` matches; fall back to first or empty object. */
    findOrFirst(items, value) {
        return (
            items?.find((item) => _.autocast(item?.value) === _.autocast(value)) ?? //
            _.first(items ?? []) ??
            {}
        );
    },

    /**
     * Determine whether the given value is considered "empty".
     *
     * This function behaves similarly to PHP's `empty()`:
     * - Treats null, undefined, false, 0, "0", "" as empty
     * - Treats empty arrays ([]), and empty objects ({}) as empty
     *
     * @function isBlank
     * @param {any} value - The value to evaluate.
     * @returns {boolean} True if the value is considered empty, otherwise false.
     *
     * @example
     * _.isBlank(null) // true
     * _.isBlank("") // true
     * _.isBlank(0) // true
     * _.isBlank([]) // true
     * _.isBlank({}) // true
     * _.isBlank("hello") // false
     * _.isBlank([1, 2]) // false
     */
    isBlank: (value) => {
        return (
            value === null || //
            value === undefined ||
            value === '' ||
            String(value).trim() === '' ||
            value === '0' ||
            value === 0 ||
            value === false ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && Object.keys(value).length === 0)
        );
    },

    /**
     * Determine whether the given value matches Laravel's "accepted" rule.
     *
     * Accepted values (Laravel standard):
     * - "yes", "on", "1", 1, "true", true
     *
     * @function isAccepted
     * @param {any} value - The value to check.
     * @returns {boolean} True if the value is considered accepted, otherwise false.
     *
     * @example
     * _.isAccepted("yes") // true
     * _.isAccepted(true) // true
     * _.isAccepted("hello") // false
     */
    isAccepted: (value) => ['yes', 'on', '1', 1, 'true', true].includes(value),

    /**
     * Determine whether the given value matches Laravel's "declined" rule.
     *
     * Declined values (Laravel standard):
     * - "no", "off", "0", 0, "false", false
     *
     * @function isDeclined
     * @param {any} value - The value to check.
     * @returns {boolean} True if the value is considered declined, otherwise false.
     *
     * @example
     * _.isDeclined("no") // true
     * _.isDeclined(0) // true
     * _.isDeclined("yes") // false
     */
    isDeclined: (value) => ['no', 'off', '0', 0, 'false', false].includes(value),

    /**
     * Determine whether the given value is a positive integer.
     *
     * A positive integer is defined as:
     * - An integer value
     * - Greater than 0 (excludes 0 and negative numbers)
     *
     * @function isPositiveInteger
     * @param {any} value - The value to check.
     * @returns {boolean} True if the value is a positive integer, otherwise false.
     *
     * @example
     * _.isPositiveInteger(1)     // true
     * _.isPositiveInteger(42)    // true
     * _.isPositiveInteger(0)     // false
     * _.isPositiveInteger(-3)    // false
     * _.isPositiveInteger(1.5)   // false
     * _.isPositiveInteger("3")   // false
     */
    isPositiveInteger: (value) => _.isInteger(value) && value > 0,

    /**
     * _.isPositiveNumber(1)     // true
     * _.isPositiveNumber(3.3)    // true
     */
    isPositiveNumber: (value) => _.isNumber(value) && value > 0,

    /**
     * Calculates the CRC32 (Cyclic Redundancy Check) checksum for a string.
     *
     * @function crc32hex
     * @description
     * Implements the IEEE 802.3 standard polynomial (0xEDB88320) for CRC32.
     * The internal CRC lookup table is generated and cached only once using a closure
     * pattern for performance and memory efficiency. The input string is first
     * converted to UTF-8 bytes to ensure correct handling of multi-byte characters.
     *
     * @param {string} str - The input string for which to calculate the hash.
     * @returns {string} The calculated CRC32 checksum as an 8-character, zero-padded hexadecimal string.
     *
     * @example
     * _.crc32hex('Hello World') // "4a17b156"
     * _.crc32hex('안녕하세요') // "e03ac670" (Example output depends on specific CRC implementation)
     * _.crc32hex('') // "00000000"
     */
    // prettier-ignore
    crc32hex: (()=>{let t=null;return e=>{if(null===t){t=[];let l;for(let r=0;r<256;r++){l=r;for(let $=0;$<8;$++)l=1&l?3988292384^l>>>1:l>>>1;t[r]=l}}let f=unescape(encodeURIComponent(e)),n=-1;for(let o=0;o<f.length;o++)n=n>>>8^t[(n^f.charCodeAt(o))&255];let u=(-1^n)>>>0,a=u.toString(16);return _.padStart(a,8,"0")}})(),

    /**
     * Automatically cast the given value into an appropriate primitive type.
     *
     * Casting rules:
     * - null, undefined → returned as-is
     * - boolean → returned as-is
     * - number → returned as-is
     * - string → converted to number only if the string is a *strict* numeric value
     *
     * A strict numeric value means:
     * - `Number(value)` is not NaN
     * - `String(Number(value)) === String(value)`
     *
     * @function autocast
     * @param {any} value - The value to cast.
     * @returns {any} The casted value if conversion rules match, otherwise the original value.
     *
     * @example
     * _.autocast("123") // 123
     * _.autocast("0") // 0
     * _.autocast("none") // "none"
     * _.autocast(true) // true
     * _.autocast(null) // null
     */
    autocast: (value) => {
        // null, undefined, boolean, number → 그대로 반환
        if (_.isNil(value) || _.isBoolean(value) || _.isNumber(value)) {
            return value;
        }

        // 문자열이 숫자로 완전히 변환 가능한지 검사
        const num = Number(value);
        return !_.isNaN(num) && String(num) === String(value) ? num : value;
    },

    /**
     * Safely parse a JSON string.
     *
     * Parsing rules:
     * - If `value` is valid JSON → return parsed result.
     * - If parsing fails → return the original value.
     *
     * @function safetyJsonParse
     * @param {any} value - The value to parse.
     * @returns {any} Parsed JSON result or the original value on failure.
     *
     * @example
     * _.safetyJsonParse('{"a":1}') // { a:1 }
     * _.safetyJsonParse('not json') // "not json"
     * _.safetyJsonParse(123) // 123
     */
    safetyJsonParse: (value) => {
        const r = _.attempt(JSON.parse, value);
        return _.isError(r) ? value : r;
    },

    /**
     * Append the correct Korean postposition (particle) based on the presence of
     * a final consonant (받침, coda) in the given word.
     *
     * This function determines whether the last character of the word is a
     * Hangul syllable with a final consonant, and selects the appropriate
     * postposition from the given pair.
     *
     * The pair must be provided in the format: "withCoda/withoutCoda".
     *
     * @function postposition
     * @param {string} word - The Korean word to which the postposition will be appended.
     * @param {string} pair - A slash-separated postposition pair (e.g. "을/를", "이/가").
     * @returns {string} The word with the correctly selected postposition appended.
     *
     * @example
     * _.postposition("사과", "을/를"); // "사과를"
     * _.postposition("집", "을/를");   // "집을"
     *
     * @example
     * _.postposition("사람", "이/가"); // "사람이"
     * _.postposition("바다", "이/가"); // "바다가"
     */
    postposition: (word = '', pair = '') => {
        if (!word || !pair) return word || '';
        word = String(word).trim();
        pair = String(pair).trim();
        const last = word[word.length - 1].charCodeAt(0);
        const hasCoda = last >= 0xac00 && last <= 0xd7a3 && (last - 0xac00) % 28 !== 0;
        return word + (pair + '/').split('/')[!hasCoda | 0];
    },

    /**
     * Create max/min object from two numbers
     * @param {number} a
     * @param {number} b
     * @returns {{max: number, min: number}}
     */
    toMaxMin: (a, b) => {
        const [max, min] = [a, b].sort((x, y) => y - x);
        return { max, min };
    },
});

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */
window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * Next we will register the CSRF Token as a common header with Axios so that
 * all outgoing HTTP requests automatically have it attached. This is just
 * a simple convenience so we don't have to attach every token manually.
 */

// let token = document.head.querySelector('meta[name="csrf-token"]');

// if (token) {
//     window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
// } else {
//     console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
// }

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

// import Echo from 'laravel-echo'
// window.Pusher = require('pusher-js');
// window.Echo = new Echo({
//     broadcaster: 'pusher',
//     key: process.env.MIX_PUSHER_APP_KEY,
//     cluster: process.env.MIX_PUSHER_APP_CLUSTER,
//     encrypted: true
// });
